/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: contact.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { FaLocationDot, FaPhone } from 'react-icons/fa6';
import { FaEnvelope } from 'react-icons/fa';
import { Button, ButtonGroup, Checkbox, Input, Textarea } from '@nextui-org/react';
import { motion } from 'framer-motion';
import { joiResolver } from '@hookform/resolvers/joi';
import emailjs from 'emailjs-com';
import Joi from 'joi';

import Assets from '@/constants/assets';
import Statics from '@/constants/statics';
import Stylings from '@/constants/stylings';
import BasicComponents from '@/shared/basic.components';
import Section from '@/shared/section';
import Utils from '@/utils';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
enum FormState {
  INITIAL = 0,
  IN_PROGRESS = 1,
  SUCCESS = 2,
  FAILED = 3
}

interface GetInTouchPayload {
  name: string;
  phone?: string;
  email: string;
  subject: string;
  message: string;
  agreeToMessages: boolean;
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Contact: FunctionComponent = () => {
  const { t } = useTranslation('contact-us');

  const [formState, setFormState] = useState<FormState>(FormState.INITIAL);

  const initialFormState: GetInTouchPayload = {
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
    agreeToMessages: false
  };

  const schema = Joi.object({
    name: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.name.empty'),
        'any.required': t('contact-form.form.errors.name.required')
      }),
    phone: Joi.string().optional().allow(''),
    email: Joi.string()
      .required()
      .email({ tlds: { allow: false } })
      .messages({
        'string.empty': t('contact-form.form.errors.email.empty'),
        'any.required': t('contact-form.form.errors.email.required'),
        'string.email': t('contact-form.form.errors.email.invalid')
      }),
    subject: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.subject.empty'),
        'any.required': t('contact-form.form.errors.subject.required')
      }),
    message: Joi.string()
      .required()
      .messages({
        'string.empty': t('contact-form.form.errors.message.empty'),
        'any.required': t('contact-form.form.errors.message.required')
      }),
    agreeToMessages: Joi.boolean()
      .required()
      .valid(true)
      .messages({
        'any.only': t('contact-form.form.errors.agreeToMessages.unchecked'),
        'any.required': t('contact-form.form.errors.agreeToMessages.required')
      })
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm<GetInTouchPayload>({
    defaultValues: initialFormState,
    resolver: joiResolver(schema)
  });

  const onSubmit = async (values: GetInTouchPayload): Promise<void> => {
    if (!values.agreeToMessages) {
      return;
    }

    setFormState(FormState.IN_PROGRESS);

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID as string,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string,
        { ...values },
        process.env.REACT_APP_EMAILJS_USER_ID as string
      )
      .then(
        ({ status }) => {
          if (status === 200) {
            localStorage.setItem('query_id', Utils.getUniqueId('user-query'));
            localStorage.setItem('query_timestamp', String(new Date().valueOf()));
            setFormState(FormState.SUCCESS);
          } else {
            setFormState(FormState.FAILED);
          }
        },
        () => setFormState(FormState.FAILED)
      );
  };

  const handleReset = (): void => {
    reset();
    setFormState(FormState.INITIAL);
  };

  useEffect(() => {
    if (localStorage.getItem('query_id')) {
      if (formState === FormState.INITIAL) {
        if (
          new Date().valueOf() - parseInt(localStorage.getItem('query_timestamp') as string, 10) <
          1 * 24 * 60 * 60 * 1000
        ) {
          setFormState(FormState.SUCCESS);
        } else {
          localStorage.removeItem('query_id');
          localStorage.removeItem('query_timestamp');
        }
      }
    }
  }, [formState]);

  return (
    <Section id="contact-us" className="bg-black/80">
      <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="col-span-1 lg:col-span-2 h-full flex flex-col items-start justify-start gap-10">
          <BasicComponents.SectionHeader
            title={t('contact-form.title')}
            subtitle={t('contact-form.subtitle')}
          />
          {formState === FormState.INITIAL && (
            <motion.div
              className="w-full flex flex-col items-center justify-start gap-5"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  y: -100,
                  opacity: 0
                },
                entry: {
                  y: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.name')}
                    placeholder={t('contact-form.form.placeholders.name')}
                    isInvalid={errors['name'] ? true : false}
                    errorMessage={errors['name']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.email')}
                    placeholder={t('contact-form.form.placeholders.email')}
                    isInvalid={errors['email'] ? true : false}
                    errorMessage={errors['email']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.phone')}
                    placeholder={t('contact-form.form.placeholders.phone')}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.subject')}
                    placeholder={t('contact-form.form.placeholders.subject')}
                    isInvalid={errors['subject'] ? true : false}
                    errorMessage={errors['subject']?.message}
                    maxLength={64}
                    {...field}
                  />
                )}
              />
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <Textarea
                    classNames={Stylings.Input}
                    radius="none"
                    size="lg"
                    label={t('contact-form.form.labels.message')}
                    placeholder={t('contact-form.form.placeholders.message')}
                    isInvalid={errors['message'] ? true : false}
                    errorMessage={errors['message']?.message}
                    maxLength={256}
                    {...field}
                  />
                )}
              />
              <Controller
                name="agreeToMessages"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className="font-inter font-semibold"
                    classNames={{
                      label: 'text-white'
                    }}
                    isSelected={field.value}
                    onValueChange={field.onChange}
                  >
                    By submitting this form, you authorize Tech Infra Steel Detailing LLC to send text messages, including both marketing and non-marketing communications, to the mobile number you have provided.
                    {errors.agreeToMessages && (
                      <span className="text-red-300 font-urbanist font-medium ml-2">
                        ({errors.agreeToMessages.message})
                      </span>
                    )}
                  </Checkbox>
                )}
              />
              <ButtonGroup>
                <Button
                  className="bg-theme-900/80 text-sm text-black hover:bg-theme-900 font-inter font-medium shadow-md drop-shadow-sm px-5 py-3"
                  radius="none"
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('contact-form.form.btns.submit')}
                </Button>
                <Button
                  className="bg-black/70 text-sm text-white hover:bg-black font-inter font-medium shadow-md drop-shadow-sm px-5 py-3"
                  radius="none"
                  onClick={handleReset}
                >
                  {t('contact-form.form.btns.reset')}
                </Button>
              </ButtonGroup>
            </motion.div>
          )}
          {formState === FormState.IN_PROGRESS && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.IN_PROGRESS_GIF.src}
                alt={Assets.Images.IN_PROGRESS_GIF.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-theme-900 font-montserrat font-bold">
                  {t('contact-form.states.in-progress.title')}
                </h4>
                <p className="text-md lg:text-lg text-white font-inter font-light">
                  {t('contact-form.states.in-progress.desc')}
                </p>
              </div>
            </div>
          )}
          {formState === FormState.FAILED && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.FAILED.src}
                alt={Assets.Images.FAILED.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-theme-900 font-montserrat font-bold">
                  {t('contact-form.states.failed.title')}
                </h4>
                <p className="text-md lg:text-lg text-white font-inter font-light">
                  {t('contact-form.states.failed.desc')}
                </p>
              </div>
            </div>
          )}
          {formState === FormState.SUCCESS && (
            <div className="w-full flex flex-col items-center justify-start gap-5">
              <img
                src={Assets.Images.SUCCESS.src}
                alt={Assets.Images.SUCCESS.alt}
                width={256}
                height={256}
              />
              <div className="w-full flex flex-col items-center justify-start gap-1">
                <h4 className="text-xl lg:text-3xl text-theme-900 font-montserrat font-bold">
                  {t('contact-form.states.success.title')}
                </h4>
                <p className="text-md lg:text-lg text-white font-inter font-light">
                  {t('contact-form.states.success.desc')}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="col-span-1 h-fit bg-black flex flex-col items-start justify-start gap-10 p-5">
          <div className="col-span-1 flex flex-col items-start justify-start gap-5">
            <div className="w-full min-h-[40px] border-b-[1px] border-b-white flex flex-col items-center justify-center">
              <motion.p
                className="w-full text-lg text-theme-900 font-montserrat font-medium uppercase"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 100,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                {t('address.title')}
              </motion.p>
            </div>
            <ul className="w-full flex flex-col items-start justify-start gap-5">
              {Statics.AddressItems.map(({ id, country, address }, idx: number) => (
                <motion.li
                  key={id}
                  className="flex flex-col items-center justify-start gap-2"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      x: 50 + idx * 10,
                      opacity: 0
                    },
                    entry: {
                      x: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <h5 className="w-full text-white font-medium">{country}</h5>
                  <a
                    className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                    href={`http://maps.google.com/?q=${address}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="w-[20px] h-[20px]">
                      <FaLocationDot />
                    </div>
                    <p className="w-[calc(100%-30px)]font-inter">{address}</p>
                  </a>
                </motion.li>
              ))}
              <motion.li
                className="flex flex-col items-center justify-start gap-2"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <a
                  className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                  href={Statics.EmailItems[0].href}
                >
                  <div className="w-[20px] h-[20px]">
                    <FaEnvelope />
                  </div>
                  <p className="w-[calc(100%-30px)]font-inter">{Statics.EmailItems[0].label}</p>
                </a>
              </motion.li>
              <motion.li
                className="flex flex-col items-center justify-start gap-2"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <a
                  className="w-full text-md text-white/60 hover:text-white flex items-center justify-start gap-[10px] transition-all duration-350"
                  href="tel:+19724489127"
                >
                  <div className="w-[20px] h-[20px]">
                    <FaPhone />
                  </div>
                  <p className="w-[calc(100%-30px)]font-inter">+19724489127</p>
                </a>
              </motion.li>
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Contact;